/**
 * Hotels API client (WP26 list + WP27 detail).
 */

import {
  hotelDetailClaimSchema,
  hotelDetailSchema,
  hotelListResponseSchema,
  type HotelDetail,
  type HotelDetailClaim,
  type HotelListResponse,
  type PipelineStatus,
} from "@carago/shared";
import { z } from "zod";
import { apiFetch, type ApiFetchOptions } from "./api.js";

export type ListHotelsParams = {
  q?: string;
  pipeline_status?: PipelineStatus | "";
  cursor?: string | null;
  limit?: number;
};

/** Build GET /v1/hotels query string from filter state. */
export function buildHotelsListQuery(params: ListHotelsParams): string {
  const qs = new URLSearchParams();
  if (params.q?.trim()) qs.set("q", params.q.trim());
  if (params.pipeline_status) {
    qs.set("pipeline_status", params.pipeline_status);
  }
  if (params.cursor) qs.set("cursor", params.cursor);
  if (params.limit != null) qs.set("limit", String(params.limit));
  const query = qs.toString();
  return query ? `/v1/hotels?${query}` : "/v1/hotels";
}

export async function listHotels(
  params: ListHotelsParams = {},
  options?: ApiFetchOptions,
): Promise<HotelListResponse> {
  const raw = await apiFetch<unknown>(buildHotelsListQuery(params), {
    method: "GET",
    ...options,
  });
  return hotelListResponseSchema.parse(raw);
}

export function hotelDetailPath(hotelId: string): string {
  return `/hotels/${hotelId}`;
}

export async function getHotelDetail(
  hotelId: string,
  options?: ApiFetchOptions,
): Promise<HotelDetail> {
  const raw = await apiFetch<unknown>(`/v1/hotels/${hotelId}`, {
    method: "GET",
    ...options,
  });
  return hotelDetailSchema.parse(raw);
}

export async function listHotelClaims(
  hotelId: string,
  options?: ApiFetchOptions,
): Promise<HotelDetailClaim[]> {
  const raw = await apiFetch<unknown>(`/v1/hotels/${hotelId}/claims`, {
    method: "GET",
    ...options,
  });
  return z.object({ items: z.array(hotelDetailClaimSchema) }).parse(raw).items;
}

export async function createHotelNote(
  hotelId: string,
  content: string,
  options?: ApiFetchOptions,
): Promise<{ id: string }> {
  return apiFetch<{ id: string }>("/v1/notes", {
    method: "POST",
    body: {
      entity_type: "hotel",
      entity_id: hotelId,
      kind: "lead",
      content,
    },
    ...options,
  });
}

export async function createHotelTask(
  input: {
    hotel_id: string;
    title: string;
    due_at?: string;
  },
  options?: ApiFetchOptions,
): Promise<{ id: string }> {
  return apiFetch<{ id: string }>("/v1/tasks", {
    method: "POST",
    body: input,
    ...options,
  });
}

/** Convert datetime-local value to ISO offset string for API. */
export function localInputToIso(value: string): string | undefined {
  if (!value.trim()) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
}

export function formatBoolTri(value: boolean | null | undefined): string {
  if (value === true) return "Ja";
  if (value === false) return "Nein";
  return "—";
}

export function formatContactSummary(
  contact: HotelListResponse["items"][number]["primary_contact"],
): string {
  if (!contact) return "Kein Kontakt";
  return `${contact.name} (${contact.email_status})`;
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("de-CH", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(iso));
}
